import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  IconButton, 
  Typography, 
  Box
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { API_BASE_URL } from '../services/api';
import { createAuthenticatedRequestOptions } from '../utils/apiUtils';
import { FileAttachment } from '../types';

interface FilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  file: File | null;
  attachment?: FileAttachment | null;
  chatId?: string | null;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ open, onClose, file, attachment, chatId }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Get the display file info (either from File object or FileAttachment)
  const getFileInfo = () => {
    if (file) {
      return {
        name: file.name,
        type: file.type,
        size: file.size
      };
    } else if (attachment) {
      return {
        name: attachment.name,
        type: attachment.type,
        size: attachment.size
      };
    }
    return null;
  };

  const fileInfo = getFileInfo();

  useEffect(() => {
    if ((!file && !attachment) || !open) {
      setContent('');
      setLoading(false);
      setError('');
      return;
    }

    const loadPreview = async () => {
      setLoading(true);
      setError('');
      setContent('');

      try {
        let fileToUse: File | null = null;

        // Priority order: 
        // 1. Direct file prop
        // 2. Pre-loaded file in attachment.file
        // 3. Fetch from server as fallback
        if (file) {
          fileToUse = file;
          // console.log(`[FilePreviewModal] Using direct file prop: ${file.name}`);
        } else if (attachment && attachment.file && attachment.file instanceof File && attachment.file.size > 0) {
          fileToUse = attachment.file;
          // console.log(`[FilePreviewModal] Using pre-loaded file from attachment: ${attachment.file.name}`);
        } else if (attachment) {
          // console.log(`[FilePreviewModal] No pre-loaded file found for ${attachment.name}, attempting server fetch`);
          
          // For image attachments with URL, try URL first
          if (attachment.type && attachment.type.startsWith('image/') && attachment.url && !attachment.url.startsWith('blob:')) {
            setContent(attachment.url);
            setLoading(false);
            return;
          }
          
          // Fallback to server fetch
          if (chatId) {
            try {
              const response = await fetch(`${API_BASE_URL}/api/chat/${chatId}/files/${encodeURIComponent(attachment.name)}`, createRequestOptions({}));
              if (response.ok) {
                const data = await response.json();
                
                // For images, convert to data URL
                if (attachment.type && attachment.type.startsWith('image/')) {
                  const dataUrl = `data:${data.contentType};base64,${data.content}`;
                  setContent(dataUrl);
                  setLoading(false);
                  return;
                }
                
                // For other files, convert to File object for text extraction
                const base64Data = data.content;
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: data.contentType });
                fileToUse = new File([blob], attachment.name, { type: data.contentType });
                // console.log(`[FilePreviewModal] Successfully fetched and created File object from server for: ${attachment.name}`);
              } else {
                setError('Failed to load file from server');
                setLoading(false);
                return;
              }
            } catch (err) {
              // console.error(`[FilePreviewModal] Server fetch failed for ${attachment.name}:`, err);
              setError('Failed to load file preview');
              setLoading(false);
              return;
            }
          } else {
            setError('File content not available');
            setLoading(false);
            return;
          }
        }

        // If we have a file to work with, process it
        if (fileToUse) {
          // Image preview
          if (fileToUse.type && fileToUse.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
              setContent(e.target?.result as string);
              setLoading(false);
            };
            reader.onerror = () => {
              setError('Failed to load image preview');
              setLoading(false);
            };
            reader.readAsDataURL(fileToUse);
            return;
          }

          // Text preview
          const reader = new FileReader();
          reader.onload = (e) => {
            const fileContent = e.target?.result as string;
            if (isProbablyText(fileContent)) {
              setContent(fileContent);
              setLoading(false);
            } else {
              // For non-text, non-image files, try extract-text API
              extractTextFromFile(fileToUse);
            }
          };
          reader.onerror = () => {
            // Fallback to extract-text API
            extractTextFromFile(fileToUse);
          };
          reader.readAsText(fileToUse);
        } else {
          setError('File not available for preview');
          setLoading(false);
        }
      } catch (err) {
        // console.error('[FilePreviewModal] Error in loadPreview:', err);
        setError('Failed to preview file');
        setLoading(false);
      }
    };

    loadPreview();
  }, [file, attachment, open, chatId]);

  const isProbablyText = (content: string): boolean => {
    let printable = 0;
    for (let i = 0; i < content.length; i++) {
      const code = content.charCodeAt(i);
      if (
        (code >= 32 && code <= 126) || // printable ASCII
        code === 9 || code === 10 || code === 13 // tab, LF, CR
      ) {
        printable++;
      }
    }
    return content.length > 0 && (printable / content.length) > 0.9;
  };

  // Use the centralized function for creating authenticated request options
  const createRequestOptions = createAuthenticatedRequestOptions;

  const extractTextFromFile = async (file: File) => {
    try {
      // Check if we have a chatId - required for the new extract-text endpoint
      if (!chatId) {
        setError('Chat ID is required for text extraction.');
        setLoading(false);
        return;
      }

      // Use the filename directly instead of converting to base64
      const response = await fetch(`${API_BASE_URL}/api/extract-text`, createRequestOptions({
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatId: chatId,
          filename: file.name
        })
      }));

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.error?.message || response.statusText);
      }

      const data = await response.json();
      if (data && data.text) {
        setContent(data.text);
      } else {
        setError('No text could be extracted from this file.');
      }
    } catch (err) {
      setError('Failed to extract text from file.');
    } finally {
      setLoading(false);
    }
  };



  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
          <img
            src="/images/belle-spinner.gif"
            alt="Loading..."
            className="belle-spinner"
            style={{ width: '60px', height: '60px' }}
          />
          <Typography variant="body2" color="text.secondary">
            Extracting text...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      );
    }

    if (!content) {
      return (
        <Typography sx={{ p: 2, textAlign: 'center' }}>
          Preview not supported for this file type.
        </Typography>
      );
    }

    // Image content
    if (fileInfo?.type && fileInfo.type.startsWith('image/')) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          <img 
            src={content} 
            alt={fileInfo.name} 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '80vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }} 
          />
        </Box>
      );
    }

    // Text content
    return (
      <pre 
        style={{ 
          maxHeight: '60vh', 
          overflow: 'auto', 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: '13px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          margin: 0
        }}
        dangerouslySetInnerHTML={{ __html: escapeHtml(content) }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          maxWidth: '90vw',
          maxHeight: '90vh',
          width: 'auto',
          height: 'auto',
          minWidth: '300px',
          minHeight: '200px',
          // For images, allow the modal to size to content
          ...(fileInfo?.type && fileInfo.type.startsWith('image/') ? {
            width: 'fit-content',
            height: 'fit-content'
          } : {
            // For text/document content, use a more constrained width
            width: '70vw',
            maxWidth: '800px'
          })
        }
      }}
    >
      <DialogContent sx={{ 
        position: 'relative', 
        p: fileInfo?.type && fileInfo.type.startsWith('image/') ? 1 : 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            }
          }}
          aria-label="Close preview"
        >
          <Close />
        </IconButton>
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewModal;